import { useCallback, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { meetingRoomsApi } from "@/lib/api/client";
import { Button, Text, Title, Group, Box, Flex, Stack } from "@mantine/core";
import { ArrowLeft, Camera, Tv, Volume2, Users, MapPin, Search } from "lucide-react";
import { useAuthStore } from "@/lib/auth";
import { toast } from "sonner";
import {
  RoomDaySchedule,
  type ScheduleEvent,
  type ScheduleFreeSlot,
  FindFreeRoomModal,
  RoomPriceBadge,
  hasPriceInfo,
  pricingTerms,
} from "@nodus/booking";
import { useApprovalSettings } from "@/hooks/use-booking-approvals";
import { approvalNoticeFor, bookingResultMessage } from "@/lib/booking-approvals";
import { warnIfPending } from "@/lib/booking-approval-toast";
import * as Sentry from "@sentry/react";

function toISODate(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDays(d: Date, n: number) {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n);
  return copy;
}

export default function MeetingRoomDetailPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [date, setDate] = useState(() => {
    const d = searchParams.get("date");
    return d ? new Date(d + "T00:00:00") : new Date();
  });
  const { user } = useAuthStore();
  const { data: approvalSettings } = useApprovalSettings();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["meeting-room-detail", roomId, toISODate(date)],
    queryFn: () => meetingRoomsApi.getRoomDetail(Number(roomId), toISODate(date)),
    enabled: !!roomId,
    staleTime: 0,
    refetchInterval: 60_000,
    retry: (count, err) =>
      // Don't retry an access denial — it won't change on retry.
      (err as { response?: { status?: number } })?.response?.status === 403 ? false : count < 2,
  });

  // 403 = room is real but on a floor the user can't reach via Axema (#165).
  const accessDenied = (error as { response?: { status?: number } } | null)?.response?.status === 403;

  const [findFreeOpen, setFindFreeOpen] = useState(false);
  const fetchFreeRooms = useCallback(
    async (durationMinutes: number) => {
      const res = await meetingRoomsApi.findFreeRooms(
        durationMinutes,
        roomId ? Number(roomId) : undefined,
      );
      return res.data;
    },
    [roomId],
  );
  const bookFreeRoom = useCallback(async (targetRoomId: number, minutes: number) => {
    const start = new Date();
    const end = new Date(start.getTime() + minutes * 60_000);
    const res = await meetingRoomsApi.bookRoom(targetRoomId, {
      title: `Booking (${minutes} min)`,
      start: start.toISOString(),
      end: end.toISOString(),
      description: "Booked via Find a free meeting room",
      source: "member_find_free",
    } as any);
    warnIfPending(res.data);
    return { end: res.data.end };
  }, []);
  const fetchWayfinding = useCallback(
    async (targetRoomId: number) => {
      const res = await meetingRoomsApi.wayfind(
        targetRoomId,
        roomId ? Number(roomId) : undefined,
      );
      return res.data;
    },
    [roomId],
  );

  const cancelMutation = useMutation({
    mutationFn: (eventId: number) => meetingRoomsApi.cancelBooking(eventId),
    onSuccess: () => {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["meeting-room-detail", roomId] });
        queryClient.invalidateQueries({ queryKey: ["my-bookings"] });
      }, 1000);
    },
  });

  const bookMutation = useMutation({
    mutationFn: ({ start, end, title, description }: { start: string; end: string; title: string; description: string }) => {
      const bookedBy = user ? `Booked by: ${user.email}${user.tenant_name ? ` (${user.tenant_name})` : ""}` : "";
      return meetingRoomsApi.bookRoom(Number(roomId), {
        title,
        start,
        end,
        description: [description, bookedBy].filter(Boolean).join("\n\n"),
      });
    },
    onSuccess: (res) => {
      toast.success(bookingResultMessage(res.data.approval, res.data.start, res.data.site_time_zone));
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["meeting-room-detail", roomId] });
        queryClient.invalidateQueries({ queryKey: ["my-bookings"] });
      }, 1000);
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.detail || "Failed to book room";
      toast.error(msg);
    },
  });

  const room = data?.data;
  const isToday = toISODate(date) === toISODate(new Date());

  // Map API data to RoomDaySchedule types
  const scheduleEvents: ScheduleEvent[] = (room?.events ?? []).map((e) => ({
    id: e.id,
    title: e.title,
    start: e.start,
    end: e.end,
    organizerName: e.organizer_tenant_name ?? undefined,
    isMyBooking: e.is_my_booking,
    description: e.description,
  }));

  const scheduleFreeSlots: ScheduleFreeSlot[] = (room?.free_slots ?? []).map((s) => ({
    start: s.start,
    end: s.end,
    durationMinutes: s.duration_minutes,
  }));

  return (
    <Flex direction="column" h="calc(100dvh - 4rem)">
      {/* Header */}
      <Box p="md" style={{ flexShrink: 0 }}>
        <Group gap="xs" mb="sm" justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
            <Button variant="subtle" size="compact-md" onClick={() => navigate("/meeting-rooms")} p={4}>
              <ArrowLeft style={{ height: 16, width: 16 }} />
            </Button>
            <Box style={{ minWidth: 0 }}>
              <Title order={4} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{room?.description || room?.name || "Meeting Room"}</Title>
              {room?.description && (
                <Text fz="xs" c="dimmed">{room.name}</Text>
              )}
            </Box>
          </Group>
          <Button
            variant="light"
            size="xs"
            leftSection={<Search size={14} />}
            onClick={() => setFindFreeOpen(true)}
          >
            Find an alternative
          </Button>
        </Group>

        {room && (
          <Group gap="sm" wrap="wrap" c="dimmed" fz={12}>
            <Text fz="xs" c="dimmed">{room.floor_name} · {room.address_name}</Text>
            {room.seats != null && (
              <Group gap={4} wrap="nowrap"><Users style={{ height: 14, width: 14 }} /><Text fz="xs" c="dimmed">{room.seats}</Text></Group>
            )}
            {room.has_camera && <Group gap={4} wrap="nowrap"><Camera style={{ height: 14, width: 14 }} /><Text fz="xs" c="dimmed">Camera</Text></Group>}
            {room.has_tv && <Group gap={4} wrap="nowrap"><Tv style={{ height: 14, width: 14 }} /><Text fz="xs" c="dimmed">Display</Text></Group>}
            {room.has_speaker && <Group gap={4} wrap="nowrap"><Volume2 style={{ height: 14, width: 14 }} /><Text fz="xs" c="dimmed">Speaker</Text></Group>}
            {room.has_floorplan && (
              <Text
                fz="xs"
                c="blue"
                component="button"
                onClick={() => navigate(`/floorplan?room=${room.id}${room.floorplan_id ? `&fp=${room.floorplan_id}` : ''}`)}
                ml="auto" style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
              >
                <MapPin style={{ height: 14, width: 14 }} />
                Show on map
              </Text>
            )}
          </Group>
        )}
        {room && hasPriceInfo(room.pricing) && (
          <Stack gap={4} mt="xs" data-testid="room-pricing">
            <Box>
              <RoomPriceBadge pricing={room.pricing} size="md" />
            </Box>
            {pricingTerms(room.pricing).map((term) => (
              <Text key={term} fz="xs" c="dimmed">{term}</Text>
            ))}
          </Stack>
        )}
      </Box>

      {isLoading && <Text fz="sm" c="dimmed" ta="center" py="xl">Loading...</Text>}
      {isError && accessDenied && (
        <Text fz="sm" c="dimmed" ta="center" py="xl">
          You don't have access to this floor. Contact your office manager if you think this is wrong.
        </Text>
      )}
      {isError && !accessDenied && (
        <Text fz="sm" c="red" ta="center" py="xl">Failed to load schedule.</Text>
      )}

      {/* Calendar — using shared RoomDaySchedule */}
      {room && !room.is_bookable && (
        <Flex align="center" justify="center" direction="column" gap="xs" flex={1} p="xl">
          <Text fz="sm" c="dimmed" ta="center">This room can't be booked.</Text>
          <Text fz="xs" c="dimmed" ta="center">Ask your office manager if you need it.</Text>
        </Flex>
      )}
      {room && room.is_bookable && (
        <RoomDaySchedule
          events={scheduleEvents}
          freeSlots={scheduleFreeSlots}
          date={date}
          dateLabel={date.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}
          isToday={isToday}
          onBook={room.is_bookable ? (start, end, title, description) => {
            bookMutation.mutate({ start, end, title, description });
          } : undefined}
          onCancel={(id) => cancelMutation.mutate(id)}
          onPrevDay={() => setDate(addDays(date, -1))}
          onNextDay={() => setDate(addDays(date, 1))}
          onDateChange={(d) => setDate(d)}
          pricing={room.pricing}
          bookingNotice={approvalNoticeFor(approvalSettings, room.pricing)}
        />
      )}

      <FindFreeRoomModal
        opened={findFreeOpen}
        onClose={() => setFindFreeOpen(false)}
        fetchFreeRooms={fetchFreeRooms}
        bookRoom={bookFreeRoom}
        fetchWayfinding={fetchWayfinding}
        onError={(err, context) =>
          Sentry.captureException(err, { tags: { feature: "fafr", context } })
        }
      />
    </Flex>
  );
}
